<template>
  <div class="antialiased min-h-screen font-sans">

    <!-- PANEL DE IMPOSICIÓN -->
    <div class="space-y-6">
      <!-- Formulario principal -->
      <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-visible">
        <div class="p-6 space-y-5">

          <!-- Delegaciones -->
          <div class="space-y-1.5">
            <label class="block text-sm font-semibold text-gray-800">
              Delegaciones a Procesar
            </label>
            <div class="multi-select-dropdown relative">
              <div
                class="flex items-center justify-between border border-slate-200 bg-white rounded-lg px-3 py-2.5 cursor-pointer hover:border-indigo-300 transition-all duration-200 min-h-[42px]"
                @click="delegationsOpen = !delegationsOpen"
              >
                <div class="flex gap-1.5 flex-wrap items-center flex-1">
                  <span v-if="selectedDelegationIds.length === 0" class="text-sm text-slate-400">
                    Seleccionar delegaciones...
                  </span>
                  <span
                    v-for="id in selectedDelegationIds"
                    :key="id"
                    class="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-1 rounded-full"
                  >
                    {{ getDelegationName(id) }}
                    <button
                      type="button"
                      class="text-indigo-400 hover:text-indigo-700 transition-colors ml-0.5 leading-none"
                      @click.stop="toggleDelegation(id)"
                    >×</button>
                  </span>
                </div>
                <i class="fas fa-chevron-down text-slate-400 text-xs ml-2 transition-transform duration-200" :class="{ 'rotate-180': delegationsOpen }"></i>
              </div>

              <div
                v-show="delegationsOpen"
                class="absolute z-30 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-200/60 overflow-hidden"
              >
                <div class="p-2 border-b border-slate-100">
                  <input
                    class="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-300 transition-colors"
                    v-model="delegationSearch"
                    placeholder="Buscar delegación..."
                  />
                </div>
                <div class="max-h-52 overflow-y-auto">
                  <div
                    v-for="del in filteredDelegations"
                    :key="del.id"
                    class="px-3 py-2.5 hover:bg-slate-50 transition-colors"
                  >
                    <label class="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        :checked="selectedDelegationIds.includes(del.id)"
                        @change="toggleDelegation(del.id)"
                        class="accent-indigo-600 w-4 h-4"
                      />
                      <span class="text-sm text-slate-700">{{ del.name }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Marco Jurídico -->
          <div class="space-y-1.5">
            <label class="block text-sm font-semibold text-gray-800">
              Marco Jurídico Aplicable
            </label>
            <select
              v-model="selectedFrameworkKey"
              class="w-full px-3 py-2.5 text-sm border border-slate-200 bg-white rounded-lg outline-none focus:border-indigo-400 transition-all duration-200 text-slate-800 cursor-pointer appearance-none"
            >
              <option v-for="(f, key) in frameworks" :key="key" :value="key">
                {{ f.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Crimes block -->
        <div v-if="currentFramework" class="border-t border-slate-100 px-6 py-5 space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-semibold text-gray-800 mb-0.5">Tipificación</p>
              <h3 class="text-sm font-bold text-slate-800">Selección de Cargos</h3>
            </div>
            <div class="relative">
              <i class="fas fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
              <input
                v-model="crimeSearchQuery"
                class="pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg bg-slate-50 outline-none focus:border-indigo-300 focus:bg-white transition-all duration-200 w-48"
                placeholder="Buscar delito..."
              />
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            <div
              v-for="crime in paginatedCrimes"
              :key="crime.id"
              class="group relative flex items-start gap-3 p-3.5 border rounded-xl cursor-pointer transition-all duration-200 select-none active:scale-[0.98]"
              :class="selectedCrimes.includes(crime.id)
                ? 'border-indigo-300 bg-indigo-50/70 shadow-sm shadow-indigo-100'
                : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'"
              @click="toggleCrime(crime.id)"
            >
              <div class="mt-0.5 flex-shrink-0">
                <div
                  class="w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200"
                  :class="selectedCrimes.includes(crime.id) ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'"
                >
                  <i v-if="selectedCrimes.includes(crime.id)" class="fas fa-check text-white text-[8px]"></i>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <span v-if="crime.courtName" class="text-[9px] font-bold uppercase tracking-wider text-indigo-500 block mb-1">
                  {{ crime.courtName }}
                </span>
                <span class="block text-[13px] font-semibold text-slate-800 leading-snug">{{ crime.name }}</span>
                <span class="block text-[10px] text-slate-400 uppercase tracking-wide mt-0.5">{{ crime.penalty }}</span>
              </div>
              <div v-if="crime.link" class="flex-shrink-0" @click.stop>
                <a
                  :href="crime.link"
                  target="_blank"
                  class="text-slate-300 hover:text-indigo-500 transition-colors duration-200"
                  title="Ver base legal"
                >
                  <i class="fas fa-arrow-up-right-from-square text-xs"></i>
                </a>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="totalCrimePages > 1" class="flex items-center justify-center gap-3 pt-2">
            <button
              class="px-3 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
              :disabled="currentCrimePage === 1"
              @click="prevPage"
            >‹ Anterior</button>
            <span class="text-xs text-slate-400 font-medium">
              {{ currentCrimePage }} / {{ totalCrimePages }}
            </span>
            <button
              class="px-3 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
              :disabled="currentCrimePage === totalCrimePages"
              @click="nextPage"
            >Siguiente ›</button>
          </div>
        </div>

        <!-- Editors -->
        <div class="border-t border-slate-100 px-6 py-5 space-y-5">

          <!-- Sentencia -->
          <div class="space-y-1.5">
            <label class="block text-sm font-semibold text-gray-800">Sentencia</label>
            <div class="border border-slate-200 rounded-xl overflow-hidden focus-within:border-indigo-400 transition-all duration-200">
              <div class="flex items-center gap-1 px-3 py-2 bg-slate-50 border-b border-slate-100">
                <button
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all duration-150 text-xs font-bold"
                  @click.prevent="formatRich('sentence', 'bold')"
                ><i class="fas fa-bold"></i></button>
                <button
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all duration-150 text-xs"
                  @click.prevent="formatRich('sentence', 'italic')"
                ><i class="fas fa-italic"></i></button>
                <button
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all duration-150 text-xs"
                  @click.prevent="formatRich('sentence', 'justifyCenter')"
                ><i class="fas fa-align-center"></i></button>
              </div>
              <div
                id="sentence-editor"
                class="min-h-[120px] p-3.5 text-sm text-slate-800 outline-none bg-white"
                contenteditable="true"
                @input="onEditorInput('sentence', $event)"
                role="textbox"
                aria-multiline="true"
              ></div>
            </div>
          </div>

          <!-- Motivación -->
          <div class="space-y-1.5">
            <label class="block text-sm font-semibold text-gray-800">Motivación Jurídica</label>
            <div class="border border-slate-200 rounded-xl overflow-hidden focus-within:border-indigo-400 transition-all duration-200">
              <div class="flex items-center gap-1 px-3 py-2 bg-slate-50 border-b border-slate-100">
                <button
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all duration-150 text-xs font-bold"
                  @click.prevent="formatRich('motivation', 'bold')"
                ><i class="fas fa-bold"></i></button>
                <button
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all duration-150 text-xs"
                  @click.prevent="formatRich('motivation', 'italic')"
                ><i class="fas fa-italic"></i></button>
                <button
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all duration-150 text-xs"
                  @click.prevent="formatRich('motivation', 'justifyCenter')"
                ><i class="fas fa-align-center"></i></button>
              </div>
              <div
                id="motivation-editor"
                class="min-h-[120px] p-3.5 text-sm text-slate-800 outline-none bg-white"
                contenteditable="true"
                @input="onEditorInput('motivation', $event)"
                role="textbox"
                aria-multiline="true"
              ></div>
            </div>
          </div>
        </div>

        <!-- Submit -->
        <div class="border-t border-slate-100 px-6 py-4 bg-slate-50/50 rounded-b-2xl">
          <md-button
            class="w-full flex items-center justify-center gap-2.5 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-bold uppercase tracking-wider rounded-xl transition-all duration-200 active:scale-[0.98] shadow-sm shadow-indigo-200 disabled:shadow-none"
            :disabled="!canSubmit || loading"
            @click="submitSentence"
          >
            <i class="fas fa-gavel text-sm"></i>
            {{ loading ? 'Registrando...' : 'Dictar Sentencia' }}
          </md-button>
        </div>
      </div>

      <!-- REGISTRO HISTÓRICO -->
      <div v-if="sentences.length" class="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <p class="text-sm font-semibold text-gray-800 mb-0.5">Registro histórico</p>
            <h3 class="text-sm font-bold text-slate-800">Libro de Sentencias Dictadas</h3>
          </div>
          <span class="text-xs font-bold bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full">
            {{ sentences.length }}
          </span>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-100">
                <th class="text-left px-6 py-3 text-sm font-semibold text-gray-800">Delegación</th>
                <th class="text-left px-6 py-3 text-sm font-semibold text-gray-800">Marco Legal</th>
                <th class="text-left px-6 py-3 text-sm font-semibold text-gray-800">Cargos</th>
                <th class="text-left px-6 py-3 text-sm font-semibold text-gray-800">Fecha</th>
                <th class="text-left px-6 py-3 text-sm font-semibold text-gray-800">Acta</th>
                <th class="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50">
              <tr
                v-for="s in sentences"
                :key="s.id"
                class="hover:bg-slate-50/60 transition-colors duration-150"
              >
                <td class="px-6 py-4">
                  <span class="font-semibold text-slate-800">{{ s.delegationName }}</span>
                </td>
                <td class="px-6 py-4">
                  <span class="inline-block bg-slate-100 text-slate-600 text-[11px] font-semibold px-2.5 py-1 rounded-md">
                    {{ frameworks[s.framework]?.name }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="cId in s.crimes"
                      :key="cId"
                      class="inline-block bg-indigo-50 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 rounded"
                    >
                      {{ getCrimeName(s.framework, cId) }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4 text-slate-500 text-xs font-medium whitespace-nowrap">
                  {{ formatDate(s.createdAt) }}
                </td>
                <td class="px-6 py-4">
                  <a
                    v-if="s.pdfUrl"
                    :href="s.pdfUrl"
                    target="_blank"
                    class="text-red-400 hover:text-red-600 transition-colors duration-200 text-lg"
                  >
                    <i class="fas fa-file-pdf"></i>
                  </a>
                </td>
                <td class="px-6 py-4 text-right">
                  <button
                    class="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all duration-200 active:scale-95"
                    @click="deleteSentence(s.id)"
                  >
                    <i class="fas fa-trash-can text-xs"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <transition enter-active-class="transition-all duration-300" enter-from-class="translate-y-2 opacity-0" enter-to-class="translate-y-0 opacity-100">
      <div
        v-if="toastVisible"
        class="fixed right-5 top-5 z-[9999] flex items-center gap-3 min-w-[280px] px-4 py-3.5 rounded-xl shadow-xl text-sm font-medium text-white"
        :class="{ 'bg-indigo-600': toastType === 'info', 'bg-emerald-600': toastType === 'success', 'bg-red-500': toastType === 'error' }"
      >
        <span class="flex-1" v-html="toastMessage"></span>
        <button
          class="text-white/70 hover:text-white transition-colors text-base leading-none"
          @click="hideToast"
        >×</button>
      </div>
    </transition>

    <!-- Confirm Modal -->
    <div
      v-if="confirmVisible"
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
    >
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div class="p-6">
          <div class="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center mb-4">
            <i class="fas fa-triangle-exclamation text-amber-500 text-sm"></i>
          </div>
          <p class="text-sm text-slate-700 leading-relaxed">{{ confirmMessage }}</p>
        </div>
        <div class="flex gap-2 px-6 pb-5">
          <button
            class="flex-1 px-4 py-2.5 text-sm font-semibold border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-all duration-200 active:scale-95"
            @click="handleConfirm(false)"
          >Cancelar</button>
          <button
            class="flex-1 px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all duration-200 active:scale-95 shadow-sm"
            @click="handleConfirm(true)"
          >Confirmar</button>
        </div>
      </div>
    </div>

  </div>
</template>
<script>
import { db } from "@/fb";
import axios from "axios";
import { collection, addDoc, getDocs, onSnapshot, doc, deleteDoc, serverTimestamp } from "firebase/firestore";

const BACKEND_URL = 'https://api.jgmun.com/api';

const FRAMEWORKS = {
  colombia: {
  name: "Código Penal (Colombia) - Ley 599 de 2000",
  crimes: [

    // ─── TÍTULO I: DELITOS CONTRA LA VIDA Y LA INTEGRIDAD PERSONAL ───
    { id: "co-001", name: "Homicidio simple", penalty: "13–25 años de prisión" },
    { id: "co-002", name: "Homicidio agravado", penalty: "25–40 años de prisión" },
    { id: "co-003", name: "Homicidio culposo", penalty: "2–6 años de prisión" },
    { id: "co-004", name: "Feminicidio", penalty: "21–50 años de prisión" },
    { id: "co-005", name: "Lesiones personales", penalty: "Multa / Prisión según gravedad" },
    { id: "co-006", name: "Lesiones culposas", penalty: "1–3 años de prisión" },
    { id: "co-007", name: "Parto o aborto preterintencional", penalty: "1–6 años de prisión" },
    { id: "co-008", name: "Aborto", penalty: "1–3 años de prisión" },
    { id: "co-009", name: "Abandono", penalty: "1–3 años de prisión" },
    { id: "co-010", name: "Omisión de socorro", penalty: "2–4 años de prisión" },

    // ─── TÍTULO II: DELITOS CONTRA PERSONAS Y BIENES PROTEGIDOS POR EL DIH ───
    { id: "co-dih-001", name: "Homicidio en persona protegida", penalty: "30–40 años de prisión" },
    { id: "co-dih-002", name: "Lesiones en persona protegida", penalty: "5–15 años de prisión" },
    { id: "co-dih-003", name: "Tortura en persona protegida", penalty: "10–20 años de prisión" },
    { id: "co-dih-004", name: "Acceso carnal violento en persona protegida", penalty: "10–18 años de prisión" },
    { id: "co-dih-005", name: "Actos sexuales violentos en persona protegida", penalty: "5–9 años de prisión" },
    { id: "co-dih-006", name: "Prostitución forzada en persona protegida", penalty: "10–18 años de prisión" },
    { id: "co-dih-007", name: "Esclavitud sexual en persona protegida", penalty: "10–18 años de prisión" },
    { id: "co-dih-008", name: "Reclutamiento ilícito (menores de edad)", penalty: "6–10 años de prisión" },
    { id: "co-dih-009", name: "Utilización de medios y métodos de guerra ilícitos", penalty: "5–15 años de prisión" },
    { id: "co-dih-010", name: "Perfidia", penalty: "8–15 años de prisión" },
    { id: "co-dih-011", name: "Actos de terrorismo en conflicto armado", penalty: "15–30 años de prisión" },
    { id: "co-dih-012", name: "Actos de barbarie", penalty: "5–15 años de prisión" },
    { id: "co-dih-013", name: "Toma de rehenes", penalty: "20–30 años de prisión" },
    { id: "co-dih-014", name: "Deportación, expulsión o traslado forzado", penalty: "10–20 años de prisión" },
    { id: "co-dih-015", name: "Ataque contra obras e instalaciones que contienen fuerzas peligrosas", penalty: "5–15 años de prisión" },
    { id: "co-dih-016", name: "Ataque contra bienes culturales y lugares de culto", penalty: "5–10 años de prisión" },
    { id: "co-dih-017", name: "Destrucción y apropiación de bienes protegidos", penalty: "5–10 años de prisión" },
    { id: "co-dih-018", name: "Exacción o contribuciones arbitrarias", penalty: "3–8 años de prisión" },
    { id: "co-dih-019", name: "Represalias", penalty: "5–10 años de prisión" },
    { id: "co-dih-020", name: "Obstaculización de tareas sanitarias y humanitarias", penalty: "5–10 años de prisión" },
    { id: "co-dih-021", name: "Omisión de medidas de socorro y asistencia humanitaria", penalty: "3–8 años de prisión" },

    // ─── TÍTULO III: DELITOS CONTRA LA LIBERTAD INDIVIDUAL ───
    { id: "co-020", name: "Secuestro simple", penalty: "16–30 años de prisión" },
    { id: "co-021", name: "Secuestro extorsivo", penalty: "20–28 años de prisión" },
    { id: "co-022", name: "Desaparición forzada", penalty: "20–30 años de prisión" },
    { id: "co-023", name: "Detención arbitraria", penalty: "3–6 años de prisión" },
    { id: "co-024", name: "Tortura", penalty: "8–15 años de prisión" },
    { id: "co-025", name: "Desplazamiento forzado", penalty: "6–12 años de prisión" },
    { id: "co-026", name: "Constreñimiento ilegal", penalty: "1–2 años de prisión" },
    { id: "co-027", name: "Constreñimiento para delinquir", penalty: "2–4 años de prisión" },
    { id: "co-028", name: "Tráfico de migrantes", penalty: "5–8 años de prisión" },
    { id: "co-029", name: "Trata de personas", penalty: "13–23 años de prisión" },
    { id: "co-030", name: "Reclutamiento ilícito (civil)", penalty: "6–10 años de prisión" },

    // ─── TÍTULO IV: DELITOS CONTRA LA LIBERTAD, INTEGRIDAD Y FORMACIÓN SEXUALES ───
    { id: "co-040", name: "Acceso carnal violento", penalty: "12–20 años de prisión" },
    { id: "co-041", name: "Acto sexual violento", penalty: "8–16 años de prisión" },
    { id: "co-042", name: "Acceso carnal o acto sexual en incapaz de resistir", penalty: "12–20 años de prisión" },
    { id: "co-043", name: "Acceso carnal abusivo con menor de 14 años", penalty: "12–20 años de prisión" },
    { id: "co-044", name: "Actos sexuales con menor de 14 años", penalty: "9–13 años de prisión" },
    { id: "co-045", name: "Pornografía con menores", penalty: "10–20 años de prisión" },
    { id: "co-046", name: "Inducción a la prostitución", penalty: "10–22 años de prisión" },
    { id: "co-047", name: "Constreñimiento a la prostitución", penalty: "9–13 años de prisión" },
    { id: "co-048", name: "Estímulo a la prostitución de menores", penalty: "10–14 años de prisión" },
    { id: "co-049", name: "Demanda de explotación sexual comercial de menores", penalty: "10–14 años de prisión" },
    { id: "co-050", name: "Acoso sexual", penalty: "1–3 años de prisión" },

    // ─── TÍTULO V: DELITOS CONTRA LA INTEGRIDAD MORAL ───
    { id: "co-060", name: "Injuria", penalty: "Multa" },
    { id: "co-061", name: "Calumnia", penalty: "1–4.5 años / Multa" },
    { id: "co-062", name: "Injuria y calumnia indirectas", penalty: "Multa" },

    // ─── TÍTULO VI: DELITOS CONTRA LA FAMILIA ───
    { id: "co-070", name: "Violencia intrafamiliar", penalty: "4–8 años de prisión" },
    { id: "co-071", name: "Maltrato mediante restricción a la libertad física", penalty: "1–3 años de prisión" },
    { id: "co-072", name: "Abandono de menores y discapacitados", penalty: "2–7 años de prisión" },
    { id: "co-073", name: "Sustracción de menor", penalty: "2–7 años de prisión" },
    { id: "co-074", name: "Incesto", penalty: "2–8 años de prisión" },
    { id: "co-075", name: "Inasistencia alimentaria", penalty: "16–54 meses de prisión" },
    { id: "co-076", name: "Adopción irregular", penalty: "3–5 años de prisión" },
    { id: "co-077", name: "Mendicidad y tráfico de menores", penalty: "1–3 años de prisión" },
    { id: "co-078", name: "Explotación de menores", penalty: "2–5 años de prisión" },

    // ─── TÍTULO VII: DELITOS CONTRA EL PATRIMONIO ECONÓMICO ───
    { id: "co-080", name: "Hurto simple", penalty: "Multa / 2–6 años según cuantía" },
    { id: "co-081", name: "Hurto agravado (calificado)", penalty: "6–14 años de prisión" },
    { id: "co-082", name: "Extorsión", penalty: "12–16 años de prisión" },
    { id: "co-083", name: "Estafa", penalty: "2–8 años de prisión" },
    { id: "co-084", name: "Abuso de confianza", penalty: "Multa / prisión menor" },
    { id: "co-085", name: "Fraude mediante cheque", penalty: "1–4 años de prisión" },
    { id: "co-086", name: "Daño en bien ajeno", penalty: "Multa / prisión menor" },
    { id: "co-087", name: "Usurpación", penalty: "2–5 años de prisión" },
    { id: "co-088", name: "Desplazamiento forzado de bienes", penalty: "2–5 años de prisión" },
    { id: "co-089", name: "Alzamiento de bienes", penalty: "1–4 años de prisión" },
    { id: "co-090", name: "Receptación", penalty: "2–8 años de prisión" },
    { id: "co-091", name: "Utilización indebida de información privilegiada", penalty: "1–5 años de prisión" },
    { id: "co-092", name: "Violación de derechos de autor", penalty: "4–8 años de prisión" },

    // ─── TÍTULO VIII: DELITOS CONTRA DERECHOS DE AUTOR ───
    { id: "co-100", name: "Defraudación a los derechos patrimoniales de autor", penalty: "4–8 años de prisión" },
    { id: "co-101", name: "Violación a los mecanismos de protección de derechos de autor", penalty: "4–8 años de prisión" },

    // ─── TÍTULO IX: DELITOS CONTRA LA FE PÚBLICA ───
    { id: "co-110", name: "Falsedad material en documento público", penalty: "3–6 años de prisión" },
    { id: "co-111", name: "Falsedad ideológica en documento público", penalty: "4–8 años de prisión" },
    { id: "co-112", name: "Falsedad en documento privado", penalty: "1–6 años de prisión" },
    { id: "co-113", name: "Falsedad para obtener prueba de hecho verdadero", penalty: "Prisión menor" },
    { id: "co-114", name: "Falsedad personal", penalty: "Multa / Prisión menor" },
    { id: "co-115", name: "Uso de documento falso", penalty: "Igual al delito base" },
    { id: "co-116", name: "Destrucción, supresión u ocultamiento de documento público", penalty: "3–10 años de prisión" },
    { id: "co-117", name: "Falsedad en certificados", penalty: "1–5 años de prisión" },

    // ─── TÍTULO X: DELITOS CONTRA EL ORDEN ECONÓMICO ───
    { id: "co-120", name: "Acaparamiento", penalty: "3–6 años de prisión" },
    { id: "co-121", name: "Especulación", penalty: "2–5 años de prisión" },
    { id: "co-122", name: "Pánico económico", penalty: "3–5 años de prisión" },
    { id: "co-123", name: "Agiotaje", penalty: "2–6 años de prisión" },
    { id: "co-124", name: "Usura", penalty: "Multa" },
    { id: "co-125", name: "Lavado de activos", penalty: "10–30 años de prisión" },
    { id: "co-126", name: "Captación masiva y habitual de dineros", penalty: "5–10 años de prisión" },
    { id: "co-127", name: "Falsedad en operaciones de financiamiento", penalty: "3–8 años de prisión" },
    { id: "co-128", name: "Manipulación fraudulenta de especies inscritas en el registro", penalty: "3–8 años de prisión" },

    // ─── TÍTULO XI: DELITOS CONTRA RECURSOS NATURALES Y MEDIO AMBIENTE ───
    { id: "co-130", name: "Daños en los recursos naturales", penalty: "1–9 años de prisión" },
    { id: "co-131", name: "Contaminación ambiental", penalty: "5–10 años de prisión" },
    { id: "co-132", name: "Experimentación ilegal en especies animales o vegetales", penalty: "1–5 años de prisión" },
    { id: "co-133", name: "Pesca ilegal", penalty: "Multa / Prisión menor" },
    { id: "co-134", name: "Caza ilegal", penalty: "Multa / Prisión menor" },
    { id: "co-135", name: "Invasión de áreas de especial importancia ecológica", penalty: "2–8 años de prisión" },
    { id: "co-136", name: "Explotación ilícita de yacimiento minero", penalty: "3–11 años de prisión" },
    { id: "co-137", name: "Ilícito aprovechamiento de los recursos biológicos", penalty: "2–6 años de prisión" },
    { id: "co-138", name: "Manejo ilícito de microorganismos nocivos", penalty: "5–9 años de prisión" },

    // ─── TÍTULO XII: DELITOS CONTRA LA SEGURIDAD PÚBLICA ───
    { id: "co-140", name: "Terrorismo", penalty: "15–30 años de prisión" },
    { id: "co-141", name: "Financiación del terrorismo", penalty: "13–22 años de prisión" },
    { id: "co-142", name: "Administración de recursos para actividades terroristas", penalty: "13–22 años de prisión" },
    { id: "co-143", name: "Instructores de actividades terroristas", penalty: "9–15 años de prisión" },
    { id: "co-144", name: "Entrenamiento para actividades terroristas", penalty: "9–15 años de prisión" },
    { id: "co-145", name: "Concierto para delinquir", penalty: "3–6 años de prisión" },
    { id: "co-146", name: "Concierto para delinquir agravado", penalty: "8–18 años de prisión" },
    { id: "co-147", name: "Amenazas", penalty: "Multa / Prisión menor" },
    { id: "co-148", name: "Instigación para delinquir", penalty: "1–3 años de prisión" },
    { id: "co-149", name: "Fabricación, tráfico y porte de armas y municiones", penalty: "9–12 años de prisión" },
    { id: "co-150", name: "Fabricación, tráfico y porte de armas de fuego o municiones de uso privativo de las FFAA", penalty: "11–15 años de prisión" },
    { id: "co-151", name: "Utilización ilegal de uniformes e insignias", penalty: "3–6 años de prisión" },
    { id: "co-152", name: "Violación de habitación ajena", penalty: "Multa / Prisión menor" },
    { id: "co-153", name: "Empleo o lanzamiento de sustancias u objetos peligrosos", penalty: "5–10 años de prisión" },

    // ─── TÍTULO XIII: DELITOS CONTRA LA SALUD PÚBLICA ───
    { id: "co-160", name: "Tráfico de estupefacientes (narcotráfico)", penalty: "10–30 años de prisión" },
    { id: "co-161", name: "Conservación o financiación de plantaciones ilícitas", penalty: "6–12 años de prisión" },
    { id: "co-162", name: "Destinación ilícita de muebles o inmuebles", penalty: "4–12 años de prisión" },
    { id: "co-163", name: "Estímulo al uso ilícito", penalty: "2–6 años de prisión" },
    { id: "co-164", name: "Suministro o formulación ilegal de medicamentos", penalty: "2–6 años de prisión" },
    { id: "co-165", name: "Corrupción de alimentos", penalty: "3–9 años de prisión" },
    { id: "co-166", name: "Imitación o simulación de alimentos o medicamentos", penalty: "Prisión" },

    // ─── TÍTULO XIV: DELITOS CONTRA MECANISMOS DE PARTICIPACIÓN DEMOCRÁTICA ───
    { id: "co-170", name: "Perturbación del certamen democrático", penalty: "4–8 años de prisión" },
    { id: "co-171", name: "Constreñimiento al sufragante", penalty: "4–8 años de prisión" },
    { id: "co-172", name: "Fraude al sufragante", penalty: "4–8 años de prisión" },
    { id: "co-173", name: "Fraude en inscripción de cédulas", penalty: "4–9 años de prisión" },
    { id: "co-174", name: "Corrupción de sufragante", penalty: "4–8 años de prisión" },
    { id: "co-175", name: "Voto fraudulento", penalty: "4–8 años de prisión" },
    { id: "co-176", name: "Favorecimiento de voto fraudulento", penalty: "4–8 años de prisión" },
    { id: "co-177", name: "Mora en la entrega de documentos relacionados con votos", penalty: "Prisión" },
    { id: "co-178", name: "Alteración de resultados electorales", penalty: "5–9 años de prisión" },
    { id: "co-179", name: "Ocultamiento, retención y posesión ilícita de cédula", penalty: "1–4 años de prisión" },

    // ─── TÍTULO XV: DELITOS CONTRA LA ADMINISTRACIÓN PÚBLICA ───
    { id: "co-180", name: "Peculado por apropiación", penalty: "6–15 años de prisión + inhabilitación" },
    { id: "co-181", name: "Peculado por uso", penalty: "Inhabilitación y prisión menor" },
    { id: "co-182", name: "Peculado por aplicación oficial diferente", penalty: "Inhabilitación" },
    { id: "co-183", name: "Peculado culposo", penalty: "1–3 años de prisión" },
    { id: "co-184", name: "Cohecho propio", penalty: "5–9 años de prisión" },
    { id: "co-185", name: "Cohecho impropio", penalty: "3–6 años de prisión" },
    { id: "co-186", name: "Cohecho por dar u ofrecer", penalty: "3–6 años de prisión" },
    { id: "co-187", name: "Concusión", penalty: "6–10 años de prisión" },
    { id: "co-188", name: "Prevaricato por acción", penalty: "3–8 años de prisión" },
    { id: "co-189", name: "Prevaricato por omisión", penalty: "2–5 años de prisión" },
    { id: "co-190", name: "Abuso de autoridad", penalty: "Multa / Suspensión" },
    { id: "co-191", name: "Usurpación de funciones públicas", penalty: "Multa / Prisión menor" },
    { id: "co-192", name: "Utilización indebida de información oficial privilegiada", penalty: "1–4 años de prisión" },
    { id: "co-193", name: "Enriquecimiento ilícito", penalty: "9–13 años de prisión" },
    { id: "co-194", name: "Celebración indebida de contratos", penalty: "4–12 años de prisión" },
    { id: "co-195", name: "Interés indebido en la celebración de contratos", penalty: "4–12 años de prisión" },
    { id: "co-196", name: "Contrato sin cumplimiento de requisitos legales", penalty: "4–12 años de prisión" },
    { id: "co-197", name: "Tráfico de influencias", penalty: "4–8 años de prisión" },
    { id: "co-198", name: "Asesoramiento y otras actuaciones ilegales", penalty: "4–8 años de prisión" },
    { id: "co-199", name: "Corrupción privada", penalty: "4–8 años de prisión" },

    // ─── TÍTULO XVI: DELITOS CONTRA LA ADMINISTRACIÓN DE JUSTICIA ───
    { id: "co-200", name: "Fraude procesal", penalty: "5–10 años de prisión" },
    { id: "co-201", name: "Falso testimonio", penalty: "6–12 años de prisión" },
    { id: "co-202", name: "Soborno", penalty: "6–15 años de prisión" },
    { id: "co-203", name: "Soborno en la actuación penal", penalty: "6–15 años de prisión" },
    { id: "co-204", name: "Fraude a resolución judicial o administrativa", penalty: "3–8 años de prisión" },
    { id: "co-205", name: "Encubrimiento", penalty: "Prisión menor" },
    { id: "co-206", name: "Favorecimiento", penalty: "2–5 años de prisión" },
    { id: "co-207", name: "Receptación (justicia)", penalty: "2–8 años de prisión" },
    { id: "co-208", name: "Fuga de presos", penalty: "Prisión menor" },
    { id: "co-209", name: "Ejercicio arbitrario de la propia razón", penalty: "Multa" },

    // ─── TÍTULO XVII: DELITOS CONTRA LA EXISTENCIA Y SEGURIDAD DEL ESTADO ───
    { id: "co-210", name: "Traición a la patria", penalty: "30–40 años de prisión" },
    { id: "co-211", name: "Actos contrarios a la defensa de la nación", penalty: "15–30 años de prisión" },
    { id: "co-212", name: "Espionaje", penalty: "8–12 años de prisión" },
    { id: "co-213", name: "Sedición", penalty: "2–8 años de prisión" },
    { id: "co-214", name: "Asonada", penalty: "1–2 años de prisión" },
    { id: "co-215", name: "Conspiración", penalty: "2–8 años de prisión" },
    { id: "co-216", name: "Seducción, usurpación y retención ilegal de mando", penalty: "Prisión" },

    // ─── DELITOS COMPETENCIA DE LA JEP (Jurisdicción Especial para la Paz) ───
    { id: "co-jep-001", name: "Crímenes de guerra (graves infracciones al DIH)", penalty: "Restricción efectiva de libertad 5–8 años (sanciones propias JEP) / Privativa si hay incumplimiento" },
    { id: "co-jep-002", name: "Crímenes de lesa humanidad", penalty: "Restricción efectiva de libertad 5–8 años (sanciones propias JEP)" },
    { id: "co-jep-003", name: "Genocidio", penalty: "Restricción efectiva de libertad 5–8 años (sanciones propias JEP)" },
    { id: "co-jep-004", name: "Ejecuciones extrajudiciales ('Falsos positivos')", penalty: "Sanción propia JEP / Hasta 20 años prisión si no reconoce verdad" },
    { id: "co-jep-005", name: "Desaparición forzada en contexto del conflicto", penalty: "Sanción propia JEP / Prisión si no reconoce" },
    { id: "co-jep-006", name: "Desplazamiento forzado masivo en el conflicto", penalty: "Sanción propia JEP" },
    { id: "co-jep-007", name: "Violencia sexual en el conflicto armado", penalty: "Sanción propia JEP / Hasta 20 años si no reconoce" },
    { id: "co-jep-008", name: "Secuestro y toma de rehenes en el conflicto", penalty: "Sanción propia JEP" },
    { id: "co-jep-009", name: "Reclutamiento y uso de menores en el conflicto", penalty: "Sanción propia JEP" },
    { id: "co-jep-010", name: "Tortura y tratos crueles en el conflicto", penalty: "Sanción propia JEP" },
    { id: "co-jep-011", name: "Ataque a la población civil", penalty: "Sanción propia JEP" },
    { id: "co-jep-012", name: "Pillaje y saqueo en contexto de conflicto", penalty: "Sanción propia JEP" },
    { id: "co-jep-013", name: "Financiación de grupos armados ilegales (conexa al conflicto)", penalty: "Sanción propia JEP" },
    { id: "co-jep-014", name: "Narcotráfico vinculado al conflicto armado (FARC–EP)", penalty: "Sanción propia JEP (delito amnistiable si fue para financiar la insurgencia)" }
  ]
},
  nuremberg_tokyo: {
  name: "Tribunales Militares Internacionales - Núremberg (1945–1946) y Tokio (1946–1948)",

  courts: {

    // ─── TRIBUNAL MILITAR INTERNACIONAL DE NÚREMBERG ───
    // Base legal: Acuerdo de Londres (8 ago 1945) + Estatuto del TMI, Arts. 6a, 6b, 6c
    nuremberg: {
      name: "Tribunal Militar Internacional (TMI) - Núremberg",
      legal_basis: "Acuerdo de Londres, 8 ago 1945 / Estatuto del TMI",
      charges: [

        // CARGO 1: Conspiración
        {
          id: "nu-01",
          charge: "Cargo 1 – Conspiración para cometer crímenes contra la paz",
          crimes: [
            { id: "nu-01-a", name: "Conspiración para planificar y librar guerras de agresión", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-01-b", name: "Conspiración para violar tratados, acuerdos y compromisos internacionales", penalty: "Muerte / Cadena perpetua" }
          ]
        },

        // CARGO 2: Crímenes contra la paz
        {
          id: "nu-02",
          charge: "Cargo 2 – Crímenes contra la paz (Art. 6a)",
          crimes: [
            { id: "nu-02-a", name: "Planeación y preparación de guerra de agresión", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-02-b", name: "Inicio de guerra de agresión", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-02-c", name: "Libramiento de guerra de agresión", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-02-d", name: "Violación de tratados internacionales (Pacto Briand-Kellogg, 1928)", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-02-e", name: "Invasión y ocupación ilegal de territorios soberanos", penalty: "Muerte / Cadena perpetua" }
          ]
        },

        // CARGO 3: Crímenes de guerra
        {
          id: "nu-03",
          charge: "Cargo 3 – Crímenes de guerra (Art. 6b)",
          crimes: [
            { id: "nu-03-a", name: "Asesinato y masacre de prisioneros de guerra", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-b", name: "Maltrato y tortura de prisioneros de guerra", penalty: "Muerte / Prisión" },
            { id: "nu-03-c", name: "Trabajo forzado de prisioneros de guerra", penalty: "Muerte / Prisión" },
            { id: "nu-03-d", name: "Asesinato de población civil en territorios ocupados", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-e", name: "Deportación de población civil para trabajo esclavo", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-f", name: "Asesinato de rehenes civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-g", name: "Pillaje y saqueo de bienes públicos y privados", penalty: "Muerte / Prisión" },
            { id: "nu-03-h", name: "Destrucción injustificada de ciudades, pueblos y aldeas", penalty: "Muerte / Prisión" },
            { id: "nu-03-i", name: "Devastación no justificada por necesidad militar", penalty: "Muerte / Prisión" },
            { id: "nu-03-j", name: "Uso de armas prohibidas (gases tóxicos, armas bacteriológicas)", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-k", name: "Experimentos médicos con prisioneros y civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-l", name: "Ejecución de comandos y soldados capturados sin juicio", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-m", name: "Asesinato de aviadores capturados (Terrorflieger)", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-n", name: "Maltrato y asesinato de marinos náufragos", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-o", name: "Utilización de mano de obra esclava de civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-03-p", name: "Saqueo de obras de arte y patrimonio cultural", penalty: "Prisión" }
          ]
        },

        // CARGO 4: Crímenes contra la humanidad
        {
          id: "nu-04",
          charge: "Cargo 4 – Crímenes contra la humanidad (Art. 6c) — Primera codificación formal en derecho internacional",
          crimes: [
            { id: "nu-04-a", name: "Exterminio de población por motivos raciales, religiosos o políticos (Holocausto)", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-b", name: "Persecución racial, política y religiosa sistemática", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-c", name: "Asesinato de masa (Massenerschiessung)", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-d", name: "Reducción a esclavitud de poblaciones civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-e", name: "Deportación de poblaciones enteras", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-f", name: "Encarcelamiento arbitrario en campos de concentración", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-g", name: "Tortura sistemática a civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-h", name: "Violación y crímenes sexuales contra civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-i", name: "Esterilización y experimentación forzada", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-j", name: "Eutanasia forzada (programa Aktion T4)", penalty: "Muerte / Cadena perpetua" },
            { id: "nu-04-k", name: "Actos inhumanos contra la población civil", penalty: "Muerte / Cadena perpetua" }
          ]
        }
      ]
    },

    // ─── TRIBUNAL MILITAR INTERNACIONAL PARA EL LEJANO ORIENTE (TOKIO) ───
    // Base legal: Carta del TMILO, 19 ene 1946 / MacArthur Special Proclamation
    tokyo: {
      name: "Tribunal Militar Internacional para el Lejano Oriente (TMILO) - Tokio",
      legal_basis: "Carta del TMILO, Proclamación Especial MacArthur, 19 ene 1946",
      charges: [

        // CLASE A: Crímenes contra la paz
        {
          id: "to-A",
          charge: "Clase A – Crímenes contra la paz",
          crimes: [
            { id: "to-A-01", name: "Conspiración para librar guerras de agresión en Asia y el Pacífico", penalty: "Muerte / Cadena perpetua" },
            { id: "to-A-02", name: "Planeación y preparación de guerra de agresión", penalty: "Muerte / Cadena perpetua" },
            { id: "to-A-03", name: "Inicio de guerra de agresión contra China (1931–1937)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-A-04", name: "Inicio de guerra de agresión contra EE.UU. (Pearl Harbor, 1941)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-A-05", name: "Inicio de guerra de agresión contra el Reino Unido y Países Bajos", penalty: "Muerte / Cadena perpetua" },
            { id: "to-A-06", name: "Inicio de guerra de agresión contra Francia (Indochina)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-A-07", name: "Violación del Pacto de París (Kellogg-Briand)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-A-08", name: "Violación de tratados bilaterales y multilaterales de neutralidad", penalty: "Muerte / Cadena perpetua" }
          ]
        },

        // CLASE B: Crímenes de guerra convencionales
        {
          id: "to-B",
          charge: "Clase B – Crímenes de guerra convencionales",
          crimes: [
            { id: "to-B-01", name: "Masacre de Nanjín (La Masacre de Nanjín, 1937–1938)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-02", name: "Asesinato y maltrato de prisioneros de guerra aliados", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-03", name: "Marchas de la muerte (Batán, Sandakán, etc.)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-04", name: "Trabajo forzado de prisioneros de guerra y civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-05", name: "Privación de comida, agua y atención médica a prisioneros", penalty: "Muerte / Prisión" },
            { id: "to-B-06", name: "Asesinato de pilotos y marineros capturados", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-07", name: "Canibalismo de soldados capturados (casos documentados)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-08", name: "Uso de armas químicas y bacteriológicas (Unidad 731)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-09", name: "Experimentos médicos con prisioneros de guerra (Unidad 731)", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-10", name: "Destrucción indiscriminada de ciudades y aldeas civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-11", name: "Saqueo y pillaje de territorios ocupados", penalty: "Muerte / Prisión" },
            { id: "to-B-12", name: "Torpedeo de barcos hospitales y de rescate", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-13", name: "Ejecución de rehenes civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "to-B-14", name: "Negación deliberada del estatuto de prisionero de guerra", penalty: "Muerte / Cadena perpetua" }
          ]
        },

        // CLASE C: Crímenes contra la humanidad
        {
          id: "to-C",
          charge: "Clase C – Crímenes contra la humanidad",
          crimes: [
            { id: "to-C-01", name: "Asesinato masivo de población civil en territorios ocupados", penalty: "Muerte / Cadena perpetua" },
            { id: "to-C-02", name: "Esclavitud sexual sistemática ('mujeres de consuelo')", penalty: "Muerte / Cadena perpetua" },
            { id: "to-C-03", name: "Violación y crímenes sexuales masivos en territorios ocupados", penalty: "Muerte / Cadena perpetua" },
            { id: "to-C-04", name: "Deportación y trabajo esclavo de poblaciones civiles", penalty: "Muerte / Cadena perpetua" },
            { id: "to-C-05", name: "Persecución política de poblaciones sometidas", penalty: "Muerte / Cadena perpetua" },
            { id: "to-C-06", name: "Tortura sistemática de civiles y prisioneros", penalty: "Muerte / Cadena perpetua" },
            { id: "to-C-07", name: "Hambre forzada de poblaciones civiles", penalty: "Muerte / Cadena perpetua" }
          ]
        }
      ]
    }
  },

  // ─── PRINCIPIOS DE NÚREMBERG (Codificados por la CDI de la ONU, 1950) ───
  // Base para el posterior derecho penal internacional (CPI / Estatuto de Roma, 1998)
  nuremberg_principles: {
    name: "Principios de Núremberg — CDI / ONU (1950)",
    note: "Estos principios establecieron que: responsabilidad individual internacional, obediencia debida NO exime de responsabilidad, cargo oficial NO exime de responsabilidad.",
    principles: [
      { id: "np-I",   name: "Responsabilidad penal internacional individual", description: "Toda persona que cometa actos que constituyan crímenes bajo el derecho internacional es responsable y está sujeta a castigo." },
      { id: "np-II",  name: "Obediencia debida no exime responsabilidad", description: "Actuar bajo órdenes del gobierno o de un superior no exime de responsabilidad si se tenía posibilidad moral de elección." },
      { id: "np-III", name: "Inmunidad de cargo oficial inaplicable", description: "El hecho de que el acusado actuara como Jefe de Estado o funcionario del gobierno no le exime de responsabilidad." },
      { id: "np-IV",  name: "Crímenes bajo derecho internacional son punibles", description: "Toda persona que cometa actos que constituyan crímenes contra la paz, de guerra o contra la humanidad es punible." },
      { id: "np-V",   name: "Definición de crímenes contra la paz", description: "Planeación, preparación, inicio o ejecución de una guerra de agresión o en violación de tratados internacionales." },
      { id: "np-VI",  name: "Definición de crímenes de guerra", description: "Violaciones a las leyes y costumbres de la guerra." },
      { id: "np-VII", name: "Definición de crímenes contra la humanidad", description: "Asesinato, exterminio, esclavitud, deportación y otros actos inhumanos contra la población civil; persecución por motivos políticos, raciales o religiosos." }
    ]
  }
},
};

export default {
  name: "SentencingManager",
  data() {
    return {
      loading: false,
      delegations: [],
      sentences: [],
      frameworks: FRAMEWORKS,
      selectedDelegationIds: [],
      crimeSearchQuery: "",
      selectedFrameworkKey: "mun",
      selectedCrimes: [],
      sentenceHtml: `<p>Por tanto, y en virtud de las pruebas practicadas, se dispone citar al interviniente para que comparezca ante este Tribunal los días 11 y 12 de junio del presente año, a fin de rendir su defensa, aportar pruebas y exponer su versión de los hechos.</p>`,
      motivationHtml: `<p>Considerando los antecedentes, la valoración de la prueba y las diligencias realizadas, este despacho estima procedente citar al afectado los días 11 y 12 de junio próximo, con el objeto de que formule su defensa técnica y dé su versión sobre los hechos imputados. Lo anterior se ordena para garantizar el derecho al debido proceso y la contradicción.</p>`,
      pdfUrl: "",
      delegationSearch: '',
      delegationsOpen: false,
      currentCrimePage: 1,
      crimesPerPage: 10,
      toastMessage: '',
      toastType: 'info',
      toastVisible: false,
      toastTimeoutId: null,
      confirmVisible: false,
      confirmMessage: '',
      confirmResolve: null,
    };
  },
  computed: {
    authContext() { return this.$delegateStore.state.authContext || {}; },
    currentFramework() { return this.frameworks[this.selectedFrameworkKey]; },
    filteredCrimes() {
      let allCrimes = [];
      if (this.currentFramework) {
        if (this.currentFramework.crimes) {
          allCrimes = this.currentFramework.crimes;
        } else if (this.currentFramework.courts) {
          // Aplanar estructura de nuremberg_tokyo (cortes -> cargos -> delitos)
          Object.values(this.currentFramework.courts).forEach(court => {
            court.charges.forEach(charge => {
              charge.crimes.forEach(crime => {
                allCrimes.push({ ...crime, courtName: court.name });
              });
            });
          });
        }
      }
      if (!this.crimeSearchQuery) return allCrimes;
      const q = this.crimeSearchQuery.toLowerCase();
      return allCrimes.filter(c => c.name.toLowerCase().includes(q) || c.penalty.toLowerCase().includes(q));
    },
    filteredDelegations() {
      if (!this.delegationSearch) return this.delegations;
      const q = this.delegationSearch.toLowerCase();
      return this.delegations.filter(d => d.name.toLowerCase().includes(q));
    },
    totalCrimePages() {
      return Math.max(1, Math.ceil((this.filteredCrimes.length || 0) / this.crimesPerPage));
    },
    paginatedCrimes() {
      const start = (this.currentCrimePage - 1) * this.crimesPerPage;
      return (this.filteredCrimes || []).slice(start, start + this.crimesPerPage);
    },
    canSubmit() { return this.selectedDelegationIds.length > 0 && this.selectedCrimes.length > 0 && (this.sentenceHtml.trim() || this.motivationHtml.trim()); }
  },
  created() {
    this.loadDelegations();
    this.fetchSentences();
  },
  mounted() {
    // Inicializar contenido de los editores sin usar v-html (evita re-render que rompe caret)
    const s = document.getElementById('sentence-editor');
    const m = document.getElementById('motivation-editor');
    if (s) s.innerHTML = this.sentenceHtml || '';
    if (m) m.innerHTML = this.motivationHtml || '';
  },
  watch: {
    crimeSearchQuery() { this.currentCrimePage = 1; },
    selectedFrameworkKey() { this.currentCrimePage = 1; },
    delegationSearch() { /* keep open */ },
    filteredCrimes() { if (this.currentCrimePage > this.totalCrimePages) this.currentCrimePage = this.totalCrimePages; }
  },
  methods: {
    async loadDelegations() {
      const ref = collection(db, `commissions/${this.authContext.cohort}/commissions/${this.authContext.commissionId}/delegates`);
      const snap = await getDocs(ref);
      const unique = {};
      snap.docs.forEach(d => {
        const del = d.data().delegation;
        if (del?.id) unique[del.id] = { id: del.id, name: del.name };
      });
      this.delegations = Object.values(unique);
    },
    fetchSentences() {
      const ref = collection(db, `commissions/${this.authContext.cohort}/commissions/${this.authContext.commissionId}/sentences`);
      onSnapshot(ref, (snap) => {
        this.sentences = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      });
    },
    toggleDelegation(id) {
      const idx = this.selectedDelegationIds.indexOf(id);
      if (idx > -1) this.selectedDelegationIds.splice(idx, 1);
      else this.selectedDelegationIds.push(id);
    },
    toggleCrime(id) {
      const idx = this.selectedCrimes.indexOf(id);
      if (idx > -1) this.selectedCrimes.splice(idx, 1);
      else this.selectedCrimes.push(id);
    },
    prevPage() {
      if (this.currentCrimePage > 1) this.currentCrimePage--;
    },
    nextPage() {
      if (this.currentCrimePage < this.totalCrimePages) this.currentCrimePage++;
    },
    goToPage(n) {
      if (n >= 1 && n <= this.totalCrimePages) this.currentCrimePage = n;
    },
    getDelegationName(id) {
      const d = this.delegations.find(x => x.id === id);
      return d ? d.name : id;
    },
    formatRich(editor, command) {
      const id = editor === 'sentence' ? 'sentence-editor' : 'motivation-editor';
      const el = document.getElementById(id);
      if (!el) return;
      el.focus();
      // Map custom command names
      const cmd = command === 'justifyCenter' ? 'justifyCenter' : command;
      try {
        document.execCommand(cmd, false, null);
      } catch (e) {
        // noop
      }
      // Update bound HTML
      if (editor === 'sentence') this.sentenceHtml = el.innerHTML;
      else this.motivationHtml = el.innerHTML;
    },
    onEditorInput(editor, ev) {
      const html = ev.target.innerHTML;
      if (editor === 'sentence') this.sentenceHtml = html;
      else this.motivationHtml = html;
    },
    stripHtml(html) {
      if (!html) return '';
      const d = document.createElement('div');
      d.innerHTML = html;
      return d.textContent || d.innerText || '';
    },
    getCrimeName(fwKey, cId) {
      const fw = this.frameworks[fwKey];
      if (!fw) return "Desconocido";

      // Búsqueda en formato plano
      if (fw.crimes) {
        return fw.crimes.find(c => c.id === cId)?.name || "Desconocido";
      }

      // Búsqueda en formato anidado (Núremberg/Tokio)
      if (fw.courts) {
        let foundName = "Desconocido";
        Object.values(fw.courts).some(court => {
          return court.charges.some(charge => {
            const crime = charge.crimes.find(c => c.id === cId);
            if (crime) { foundName = crime.name; return true; }
            return false;
          });
        });
        return foundName;
      }
      return "Desconocido";
    },
    formatDate(ts) {
      return ts?.toDate ? ts.toDate().toLocaleString() : 'Reciente';
    },
    showToast(message, type = 'info', timeout = 6000) {
      this.toastMessage = message;
      this.toastType = type;
      this.toastVisible = true;
      if (this.toastTimeoutId) clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = setTimeout(() => {
        this.toastVisible = false;
        this.toastTimeoutId = null;
      }, timeout);
    },
    hideToast() {
      this.toastVisible = false;
      if (this.toastTimeoutId) { clearTimeout(this.toastTimeoutId); this.toastTimeoutId = null; }
    },
    showConfirm(message) {
      return new Promise((resolve) => {
        this.confirmMessage = message;
        this.confirmVisible = true;
        this.confirmResolve = resolve;
      });
    },
    handleConfirm(val) {
      this.confirmVisible = false;
      if (this.confirmResolve) {
        this.confirmResolve(val);
        this.confirmResolve = null;
      }
    },
    async generateSentencePDF(delName) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const W = 210; const H = 297;
  const ML = 22; const MR = W - 22; // Márgenes institucionales simétricos

  // ── Helpers de Formato Clásico Monocromático ───────────────────────
  const setBlack = () => doc.setTextColor(0, 0, 0);
  const serif    = (style = 'normal') => doc.setFont('times', style);

  // ── 1. Encabezado Centrado Global ──────────────────────────────────
  setBlack();
  doc.setFontSize(13);
  serif('bold');
  doc.text('ADMINISTRACIÓN DE JUSTICIA', W / 2, 22, { align: 'center' });

  doc.setFontSize(10.5);
  serif('bold');
  doc.text('TRIBUNAL DE ENJUICIAMIENTO Y PROTOCOLO', W / 2, 27, { align: 'center' });

  doc.setFontSize(9.5);
  serif('normal');
  doc.text('SALA DE DEFINICIÓN DE SITUACIONES JURÍDICAS', W / 2, 32, { align: 'center' });

  // Fecha en formato centralizado (tal como la imagen de referencia)
  const fechaEmision = new Date().toLocaleString('es-CO', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
  doc.setFontSize(10);
  serif('normal');
  doc.text(`${fechaEmision}`, W / 2, 42, { align: 'center' });

  // ── 2. Cuadro Enmarcado de Datos del Expediente ─────────────────────
  const boxTop = 48;
  const boxHeight = 42;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(ML, boxTop, MR - ML, boxHeight, 'S'); // Cuadro exterior negro

  const expNo = `SENT-${Date.now().toString().slice(-8)}`;
  const magistrado = this.$delegateStore?.state?.delegateData?.name ?? 'Presidencia del Tribunal';

  // Mapeo de datos dentro del marco formal
  const metadata = [
    { label: 'Radicado expediente SICE:', value: expNo },
    { label: 'Delegación Imputada:',         value: delName },
    { label: 'Magistrado / Juez:',           value: magistrado },
    { label: 'Asunto:',                      value: 'Acta Oficial de Acusación y Fallo Jurídico' }
  ];

  let itemY = boxTop + 7;
  metadata.forEach(item => {
    // Etiqueta izquierda en negrita
    doc.setFontSize(9.5);
    serif('bold');
    doc.text(item.label, ML + 5, itemY);

    // Valor tabulado a la derecha en texto normal
    serif('normal');
    doc.text(item.value, ML + 52, itemY); 
    itemY += 8;
  });

  // ── 3. Número de Resolución Central ────────────────────────────────
  let y = boxTop + boxHeight + 12;
  doc.setFontSize(11);
  serif('bold');
  doc.text(`Resolución N.° ${expNo.replace('SENT-', '')} de ${new Date().getFullYear()}`, W / 2, y, { align: 'center' });
  
  y += 12;

  // Helper para títulos de sección con número romano (Ej: I. ASUNTO)
  const printSectionHeader = (title) => {
    doc.setFontSize(10.5);
    serif('bold');
    doc.text(title.toUpperCase(), W / 2, y, { align: 'center' });
    return y + 8;
  };

  // ── 4. Secciones del Documento ─────────────────────────────────────
  
  // I. Marco Jurídico
  y = printSectionHeader('I. Marco Jurídico Aplicable');
  doc.setFontSize(10);
  serif('normal');
  const fwName = this.currentFramework?.name ?? 'Protocolo General';
  const splitFw = doc.splitTextToSize(fwName, MR - ML);
  
  splitFw.forEach(line => {
    if (y > H - 25) { doc.addPage(); y = 25; }
    doc.text(line, ML, y);
    y += 5.5;
  });
  y += 6;

  // II. Cargos
  if (y > H - 35) { doc.addPage(); y = 25; }
  y = printSectionHeader('II. Cargos y Tipificación');
  
  const crimes = this.selectedCrimes ?? [];
  if (crimes.length === 0) {
    if (y > H - 25) { doc.addPage(); y = 25; }
    serif('normal');
    doc.text('No se registran cargos formalizados en este folio.', ML, y);
    y += 6;
  } else {
    crimes.forEach((cId, index) => {
      const name = this.getCrimeName(this.selectedFrameworkKey, cId);
      const penalty = this.currentFramework?.crimes?.find(c => c.id === cId)?.penalty ?? '';

      const cargoText = `${index + 1}. Cargo penal: ${name}`;
      const splitCargo = doc.splitTextToSize(cargoText, MR - ML);

      splitCargo.forEach(line => {
        if (y > H - 25) { doc.addPage(); y = 25; }
        serif('bold');
        doc.text(line, ML, y);
        y += 5.5;
      });

      if (penalty) {
        if (y > H - 25) { doc.addPage(); y = 25; }
        serif('italic');
        doc.text(`Medida punitiva aplicable: ${penalty}`, ML + 4, y);
        y += 6;
      }
      y += 2;
    });
  }
  y += 6;

  // III. Sentencia
  if (y > H - 35) { doc.addPage(); y = 25; }
  doc.setFontSize(10);
  if (this.sentenceHtml?.trim()) {
    y = printSectionHeader('III. Sentencia');
    serif('normal');

    const renderHtmlOrText = async (htmlContent) => {
      if (!htmlContent) return;
      // sanitize non-breaking spaces
      htmlContent = htmlContent.replace(/&nbsp;/g, ' ');
      if (/<[a-z][\s\S]*>/i.test(htmlContent)) {
        const container = document.createElement('div');
        container.innerHTML = htmlContent;
        try {
          // Only try doc.html if html2canvas is available to avoid loader errors
          if (typeof window !== 'undefined' && (window.html2canvas || window.html2canvas === undefined)) {
            // If html2canvas global exists, use doc.html; otherwise fallback
            if (window.html2canvas) {
              await new Promise((resolve, reject) => {
                try {
                  doc.html(container, {
                    x: ML,
                    y,
                    html2canvas: { scale: 0.6 },
                    callback: () => { resolve(); }
                  });
                } catch (err) { reject(err); }
              });
              y += 8;
            } else {
              // html2canvas not present -> fallback to plain text
              const plain = container.textContent || container.innerText || htmlContent.replace(/&nbsp;/g, ' ');
              const splitObs = doc.splitTextToSize(plain, MR - ML);
              splitObs.forEach(line => {
                if (y > H - 25) { doc.addPage(); y = 25; }
                doc.text(line, ML, y);
                y += 5.5;
              });
            }
          } else {
            // Worker context or unexpected -> fallback
            const plain = container.textContent || container.innerText || htmlContent.replace(/&nbsp;/g, ' ');
            const splitObs = doc.splitTextToSize(plain, MR - ML);
            splitObs.forEach(line => {
              if (y > H - 25) { doc.addPage(); y = 25; }
              doc.text(line, ML, y);
              y += 5.5;
            });
          }
        } catch (e) {
          const plain = container.textContent || container.innerText || htmlContent.replace(/&nbsp;/g, ' ');
          const splitObs = doc.splitTextToSize(plain, MR - ML);
          splitObs.forEach(line => {
            if (y > H - 25) { doc.addPage(); y = 25; }
            doc.text(line, ML, y);
            y += 5.5;
          });
        }
      } else {
        const splitObs = doc.splitTextToSize(htmlContent, MR - ML);
        splitObs.forEach(line => {
          if (y > H - 25) { doc.addPage(); y = 25; }
          doc.text(line, ML, y);
          y += 5.5;
        });
      }
    };

    await renderHtmlOrText(this.sentenceHtml);
    y += 6;
  }

  // IV. Motivación Jurídica
  if (this.motivationHtml?.trim()) {
    if (y > H - 35) { doc.addPage(); y = 25; }
    y = printSectionHeader('IV. Motivación Jurídica');
    serif('normal');
    // reuse render helper
    let htmlToRender = this.motivationHtml.replace(/&nbsp;/g, ' ');
    const containerM = document.createElement('div');
    containerM.innerHTML = htmlToRender;
    try {
      if (typeof window !== 'undefined' && (window.html2canvas || window.html2canvas === undefined)) {
        if (window.html2canvas) {
          await new Promise((resolve, reject) => {
            try {
              doc.html(containerM, {
                x: ML,
                y,
                html2canvas: { scale: 0.6 },
                callback: () => { resolve(); }
              });
            } catch (err) { reject(err); }
          });
          y += 8;
        } else {
          const plain = containerM.textContent || containerM.innerText || htmlToRender;
          const splitObs = doc.splitTextToSize(plain, MR - ML);
          splitObs.forEach(line => {
            if (y > H - 25) { doc.addPage(); y = 25; }
            doc.text(line, ML, y);
            y += 5.5;
          });
        }
      } else {
        const plain = containerM.textContent || containerM.innerText || htmlToRender;
        const splitObs = doc.splitTextToSize(plain, MR - ML);
        splitObs.forEach(line => {
          if (y > H - 25) { doc.addPage(); y = 25; }
          doc.text(line, ML, y);
          y += 5.5;
        });
      }
    } catch (e) {
      const plain = containerM.textContent || containerM.innerText || htmlToRender;
      const splitObs = doc.splitTextToSize(plain, MR - ML);
      splitObs.forEach(line => {
        if (y > H - 25) { doc.addPage(); y = 25; }
        doc.text(line, ML, y);
        y += 5.5;
      });
    }
    y += 6;
  }

  // Si no había ninguno de los dos, incluir texto por defecto
  if (!this.sentenceHtml?.trim() && !this.motivationHtml?.trim()) {
    const defaultText = 'Sin motivación adicional registrada por este despacho.';
    const splitObs = doc.splitTextToSize(defaultText, MR - ML);
    splitObs.forEach(line => {
      if (y > H - 25) { doc.addPage(); y = 25; }
      doc.text(line, ML, y);
      y += 5.5;
    });
  }

  y += 12;

  // ── 5. Notifíquese y Firma Centrada ─────────────────────────────
  if (y > H - 70) { doc.addPage(); y = 25; }

  // Texto centrado de cierre
  doc.setFontSize(11);
  serif('bold');
  doc.text('NOTIFÍQUESE Y CÚMPLASE', W / 2, y, { align: 'center' });
  y += 14;

  // Línea de firma centrada
  if (y > H - 45) { doc.addPage(); y = 25; }
  const sigWidth = 80;
  const sigX = (W - sigWidth) / 2;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(sigX, y, sigX + sigWidth, y);

  doc.setFontSize(9);
   y += 6;
  doc.text(magistrado.toUpperCase(), W / 2, y, { align: 'center' });

  y += 6;
  serif('bold');
  doc.text('MAGISTRADO / JUEZ PRESENTE', W / 2, y, { align: 'center' });

 

  // ── 6. Pie de Página y Cierre de Folio ─────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.line(ML, H - 15, MR, H - 15); // Línea divisoria inferior

    doc.setFontSize(8);
    serif('normal');
    
    doc.text('Documento de validez protocolaria emitido por la Administración de Justicia.', W / 2, H - 10, { align: 'center' });
    doc.text(`Página ${i} de ${pageCount}`, MR, H - 10, { align: 'right' });
    doc.text(expNo, ML, H - 10);
  }

  // ── 7. Metadatos del Documento ─────────────────────────────────────
  doc.setProperties({
    title:   `Resolución Judicial — ${delName}`,
    subject: 'Acta de Acusación y Fallo Jurídico',
    author:  `Administración de Justicia · ${magistrado}`,
    creator: 'Plataforma Judicial SICE',
  });

  return doc.output('blob');
},
    async submitSentence() {
      const ok = await this.showConfirm("¿Confirma la emisión de estas sentencias? Se generará un acta oficial PDF para cada delegación.");
      if (!ok) return;
      this.loading = true;
      try {
        for (const delId of this.selectedDelegationIds) {
          const del = this.delegations.find(d => d.id === delId);
          
          // 1. Generar el PDF en memoria
          const pdfBlob = await this.generateSentencePDF(del.name);

          // 2. Subir a tu API para obtener URL persistente
          const formData = new FormData();
          formData.append('file', pdfBlob, `Sentencia_${del.name}.pdf`);
          const uploadRes = await axios.post(`${BACKEND_URL}/docs-upload`, formData);
          
          const fileKey = uploadRes.data.key;
          const encodedKey = fileKey.split('/').map(encodeURIComponent).join('/');
          const urlRes = await fetch(`${BACKEND_URL}/docs/${encodedKey}`);
          const finalPdfUrl = urlRes.url;
          const payload = {
            delegationId: delId,
            delegationName: del.name,
            framework: this.selectedFrameworkKey,
            crimes: this.selectedCrimes,
            sentenceHtml: this.sentenceHtml,
            motivationHtml: this.motivationHtml,
            pdfUrl: finalPdfUrl,
            createdAt: serverTimestamp(),
            judge: this.$delegateStore.state.delegateData?.name || "Presidente"
          };
          
          const docRef = await addDoc(collection(db, `commissions/${this.authContext.cohort}/commissions/${this.authContext.commissionId}/sentences`), payload);
          
          // Notificar a la delegación
          await addDoc(collection(db, "notifications"), {
            recipientId: delId,
            senderName: "Presidencia / Mesa",
            message: `[SENTENCIA - ${this.currentFramework.name}] ${this.stripHtml(this.motivationHtml || this.sentenceHtml)}`,
            evidenceId: docRef.id,
            type: "sentencing",
            pdfUrl: finalPdfUrl,
            createdAt: new Date().toISOString(),
            read: false,
            cohort: this.authContext.cohort,
            commissionId: this.authContext.commissionId
          });
        }

        this.selectedCrimes = [];
        this.selectedDelegationIds = [];
        this.sentenceHtml = "";
        this.motivationHtml = "";
        const sEl = document.getElementById('sentence-editor');
        const mEl = document.getElementById('motivation-editor');
        if (sEl) sEl.innerHTML = '';
        if (mEl) mEl.innerHTML = '';
        this.showToast('Sentencias dictadas, actas generadas y registradas exitosamente.', 'success');
      } catch (err) {
        this.showToast('Error al procesar la sentencia: ' + (err.message || String(err)), 'error');
      } finally {
        this.loading = false;
      }
    },
    async deleteSentence(id) {
      const ok = await this.showConfirm("¿Eliminar este registro del libro de sentencias?");
      if (!ok) return;
      await deleteDoc(doc(db, `commissions/${this.authContext.cohort}/commissions/${this.authContext.commissionId}/sentences`, id));
    }
  }
};
</script>
